import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface Job {
  _id: string;
  title: string;
  content: string;
  priority: number;
  isPublished: boolean;
  createdAt: string;
}

const JobsDisplay: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchJobs();
  }, []);

  const toggleJob = (jobId: string) => {
    setExpandedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/jobs');
      console.log('Jobs API response:', response.data);
      // Handle both formats: array or {success, data}
      const jobsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.data || []);
      console.log('Jobs data:', jobsData);
      // Only show published jobs, sorted by priority
      const publishedJobs = jobsData
        .filter((job: Job) => job.isPublished)
        .sort((a: Job, b: Job) => b.priority - a.priority);
      console.log('Published jobs:', publishedJobs);
      setJobs(publishedJobs);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setLoading(false);
    }
  };

  // Format content to ensure proper line breaks and spacing
  const formatContent = (content: string) => {
    let formatted = content.trim();
    
    // Convert markdown-style bold (**text** or __text__) to HTML <strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>');
    
    // Convert markdown-style italic (*text* or _text_) to HTML <em>
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>');
    
    // Convert line breaks to <br>
    formatted = formatted.replace(/\n/g, '<br>');
    
    // If content already has HTML paragraph tags, return as is
    if (formatted.includes('<p>') || formatted.includes('<ul>') || formatted.includes('<ol>') || formatted.includes('<h')) {
      return formatted;
    }
    
    // If plain text, wrap in paragraph
    return `<p>${formatted}</p>`;
  };

  return (
    <div className="text-gray-100 font-sans">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Be part of the future of blockchain innovation. We're looking for talented individuals 
              who are passionate about building the next generation of decentralized technologies.
            </p>
          </div>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="py-20 px-6" style={{minHeight: 'calc(100vh - 600px)'}}>
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-dusk-copper"></div>
              <p className="text-gray-400 mt-4">Loading positions...</p>
            </div>
          ) : jobs.length === 0 ? null : (
            <div className="space-y-8">
              {jobs.map((job) => (
                <div
                  key={job._id}
                  className="dashboard-block"
                >
                  {/* Collapsible Header */}
                  <button
                    onClick={() => toggleJob(job._id)}
                    className="w-full p-8 text-left flex items-center justify-between hover:bg-[rgba(243,235,226,0.04)] transition-colors"
                  >
                    <h2 className="text-3xl font-bold text-white">{job.title}</h2>
                    <svg
                      className={`w-6 h-6 text-gray-400 transition-transform ${
                        expandedJobs.has(job._id) ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Collapsible Content */}
                  {expandedJobs.has(job._id) && (
                    <div className="px-8 pb-8">
                      <div 
                        className="prose prose-invert max-w-none text-gray-300 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: formatContent(job.content) }}
                      />

                      <div className="mt-8 pt-8 border-t border-gray-700">
                        <h3 className="text-xl font-semibold text-white mb-4">Ready to Apply?</h3>
                        <p className="text-gray-400 mb-6">
                          Send your resume and cover letter to <a href="mailto:careers@duskproof.com" className="text-blue-400 hover:text-blue-300">careers@duskproof.com</a> with the position title in the subject line.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default JobsDisplay;

